"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {NeoButton, NeoDatePicker, NeoTextBox} from "@/components/forms";
import {UploadFormField} from "@/components/pages/expensereports/uploadform";
import {NeoHeader, PlusIcon} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {ExpenseService} from "@/service/expense.service";
import {FleetService} from "@/service/fleet.service";
import {yupResolver} from "@hookform/resolvers/yup";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {AiOutlineLoading} from "react-icons/ai";
import Select from "react-select";
import * as yup from "yup";

export type FormMaintance = {
  vehicleId: string;
  id?: string;
  date?: Date | null;
  totalAmount: number;
  expenseType: number;
  vendorId?: number | null;
  description?: string | null;
};

type MaintanceFormProps = {
  data: any;
  onSubmit: (formValues: any) => void | boolean | Promise<boolean | void>;
  onClose: any;
};

type FormViewMode = "single" | "bulkPreview";

const FORM_VIEW_OPTIONS = [
  {
    value: "single" as const,
    label: "Single Entry",
    description: "Manual form"
  },
  {
    value: "bulkPreview" as const,
    label: "Bulk Upload",
    description: "Excel upload"
  }
] as const;

export const ExpenseMaintanceForm: React.FC<MaintanceFormProps> = ({
  data,
  onSubmit,
  onClose
}) => {
  const maxDate = useMemo(() => new Date(), []);
  const isCreateMode = !data?.id;
  const {filterContext} = useNeoContext();
  const [loading, setLoading] = useState(false);
  const [loginUserId, setLoginUserId] = useState("");
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [servicetypes, setServiceTypes] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [defaultformdata, setdefaultformdata] = useState<any>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalName, setModalName] = useState("");
  const [modalData, setModalData] = useState(null);
  const [expenseSettingType, setExpenseSettingType] = useState(null);
  const [activeFormView, setActiveFormView] = useState<FormViewMode>("single");
  const [bulkExcelFile, setBulkExcelFile] = useState<File | null>(null);
  const [bulkExcelError, setBulkExcelError] = useState("");
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const toNumOrUndef = (v: unknown): number | undefined => {
    if (v == null) return undefined;
    if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
    if (typeof v === "string") {
      const s = v.trim().replace(/,/g, "");
      if (!s) return undefined;
      const n = Number(s);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  };

  const schema = yup.object().shape({
    vehicleId: yup.string().required("Please select vehicle"),
    id: yup.string().optional(),
    date: yup
      .date()
      .transform((v, o) => {
        if (o === "" || o == null) return null;
        const d = new Date(o);
        return isNaN(d.getTime()) ? null : d;
      })
      .nullable()
      .typeError("Enter a valid date")
      .max(today, "Date cannot be in the future"),
    expenseType: yup.number().nullable().required("Please select service type"),
    totalAmount: yup
      .number()
      .nullable()
      .transform(toNumOrUndef)
      .typeError("Total cost must be a number")
      .moreThan(0, "Total cost must be greater than 0")
      .max(100000, "Total cost must be less than or equal to 1,00,000")
      .required("Please enter total cost"),
    vendorId: yup.number().nullable(),
    description: yup.string().nullable().default("")
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: {isSubmitting, errors}
  } = useForm<FormMaintance>({
    mode: "onChange",
    resolver: yupResolver<FormMaintance>(schema),
    shouldUnregister: false
  });

  const mapApiToForm = (apiData: any): FormMaintance => {
    return {
      vehicleId: apiData?.vehicleId ?? "",
      date: apiData?.date,
      expenseType: apiData?.expenseType ?? "",
      totalAmount: toNumOrUndef(apiData?.totalAmount) ?? 0,
      vendorId: apiData?.vendorId ?? "",
      description: apiData?.description ?? ""
    };
  };

  const handleApiResponse = (apiResponse: any) => {
    const formValues = mapApiToForm(apiResponse);
    reset(formValues);
  };

  useEffect(() => {
    setValue("date", maxDate);
    if (filterContext?.client?.institutionUserId) {
      setLoginUserId(filterContext.client.institutionUserId);
    }
  }, [filterContext?.client?.institutionUserId]);

  const fetchVehicleDatas = useCallback(async () => {
    try {
      const requestData = {
        filters: {
          branch: [loginUserId]
        }
      };
      const response = await ExpenseService.getvehicles(requestData);
      if (
        response?.data?.status === "success" &&
        response?.data?.data?.reportData
      ) {
        setVehicles(response.data.data.reportData);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId]);

  const fetchfleetdatas = useCallback(async () => {
    try {
      const response = await FleetService.getfleetdatas(loginUserId);
      if (response?.data?.status === "success" && response?.data?.data) {
        const list = response.data.data?.["repairExpenseTypes"] ?? [];
        const updatedlist = [
          {id: "", name: "Please select Service Types "},
          ...list
        ];
        setServiceTypes(updatedlist);
        const vendorlist = response.data.data?.["vendors"] ?? [];
        const updatedvendorList = [
          {id: "", name: "Please select Vendor/Workshop"},
          ...vendorlist
        ];
        setVendors(updatedvendorList);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId]);

  const getmaintancedata = useCallback(async () => {
    const requestdata = {
      expenseType: 2,
      expenseId: data?.id
    };
    try {
      const response = await ExpenseService.getexpenseview(
        loginUserId,
        requestdata
      );
      if (response?.data?.status === "success" && response?.data?.data) {
        const responsedata = response?.data?.data;
        const responsemaintancedata = responsedata.maintenanceExpense;
        setAttachments(
          Array.isArray(responsemaintancedata?.documents)
            ? responsemaintancedata.documents
            : []
        );
        const maintancedata: any = {
          vehicleId: responsemaintancedata?.vehicleId,
          date: responsemaintancedata?.date,
          totalAmount: responsemaintancedata?.totalAmount,
          description: responsemaintancedata?.description,
          vendorId: responsemaintancedata?.vendorId,
          expenseType: responsemaintancedata?.serviceId,
          document: responsemaintancedata.document,
          id: data?.id
        };
        handleApiResponse(maintancedata);
        setdefaultformdata(maintancedata);
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  }, [loginUserId, data?.id]);

  useEffect(() => {
    if (!loginUserId) return;
    (async () => {
      try {
        await fetchVehicleDatas();
        await fetchfleetdatas();
        if (data?.id) {
          await getmaintancedata();
        }
      } catch (err) {
        toast.error(
          "Sorry! We have a technical issue. Please try again later."
        );
      }
    })();
  }, [loginUserId, fetchVehicleDatas, fetchfleetdatas]);

  const onDateChange = (name: keyof FormMaintance, event: any) => {
    clearErrors(name);
    setValue(name, event, {shouldDirty: true, shouldValidate: true});
  };

  const onFormSubmit = useCallback(
    async (form: FormMaintance) => {
      const payload = {
        ...form,
        id: data?.id ? String(data.id) : "",
        attachments
      };
      try {
        await onSubmit(payload);
      } catch (err) {
        toast.error(
          "Sorry! We have a technical issue. Please try again later."
        );
      }
    },
    [attachments, onSubmit]
  );

  const onCancel = useCallback(() => {
    setAttachments(
      Array.isArray(defaultformdata?.documents) ? defaultformdata.documents : []
    );
    if (data?.id) {
      getmaintancedata();
    } else {
      handleApiResponse(defaultformdata);
      setValue("date", maxDate);
    }
  }, [reset, defaultformdata?.documents]);

  const openPopup = (type: any) => {
    setExpenseSettingType(type);
    setModalName("fleetform");
    setModalOpen(true);
  };

  const isBulkPreviewActive = isCreateMode && activeFormView === "bulkPreview";

  const handleFormViewChange = (view: FormViewMode) => {
    if (view === "single") {
      setAttachments(
        Array.isArray(defaultformdata?.documents)
          ? defaultformdata.documents
          : []
      );
      handleApiResponse(defaultformdata);
      if (!data?.id) {
        setValue("date", maxDate);
      }
    }

    setActiveFormView(view);
    setBulkExcelError("");
  };

  const validateBulkExcelFile = (file: File) => {
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    return ["xlsx", "xls"].includes(extension);
  };

  const handleBulkExcelChange = (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;

    if (!validateBulkExcelFile(file)) {
      setBulkExcelFile(null);
      setBulkExcelError("Please upload an .xlsx or .xls file.");
      toast.error("Please upload an .xlsx or .xls file.");
      return;
    }

    setBulkExcelFile(file);
    setBulkExcelError("");
  };

  const clearBulkExcelFile = () => {
    setBulkExcelFile(null);
    setBulkExcelError("");
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = "";
    }
  };

  const handleBulkUploadSubmit = useCallback(async () => {
    if (!bulkExcelFile) {
      const errorMessage = "Please select an Excel file to continue.";
      setBulkExcelError(errorMessage);
      toast.error(errorMessage);
      return;
    }

    setBulkExcelError("");
    setIsBulkUploading(true);
    try {
      await onSubmit({
        bulkUpload: true,
        excelFile: bulkExcelFile
      });
    } catch (error: any) {
      const errorMessage =
        typeof error?.message === "string" && error.message.trim()
          ? error.message
          : "Sorry! We have a technical issue. Please try again later.";

      setBulkExcelError(errorMessage);

      if (!error?.message) {
        toast.error(errorMessage);
      }
    } finally {
      setIsBulkUploading(false);
    }
  }, [bulkExcelFile, onSubmit]);

  const handleTemplateAction = async () => {
    try {
      await ExpenseService.downloadExpenseTemplate({
        type: "maintenance",
        vehicles,
        serviceTypes: servicetypes,
        vendors
      });
      toast.success("Template downloaded.");
    } catch {
      toast.error("Unable to download template. Please try again later.");
    }
  };

  const handleFormSubmit = async (data: any) => {
    const requestData: Record<string, any> = {
      type: expenseSettingType,
      name: data.name,
      phone: data.phone,
      address: data.address
    };

    try {
      const response = await FleetService.addfleetdata(
        requestData,
        loginUserId
      );
      if (response?.data?.status === "success") {
        setModalOpen(false);
        await fetchfleetdatas();
        toast.success(response?.data?.message);
      } else {
        toast.error(response?.data?.message || "Something went wrong.");
      }
    } catch {
      toast.error("Sorry! We have a technical issue. Please try again later.");
    }
  };
  return (
    <>
      <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-5">
        <div className="mb-5 flex-row font-bold">
          <NeoHeader message="Maintenance Expense" />
        </div>

        {isCreateMode && (
          <div className="mb-5 grid w-full max-w-[420px] grid-cols-2 gap-2 rounded-[20px] border border-slate-200 bg-white p-2 shadow-[0_12px_28px_-26px_rgba(15,23,42,0.2)]">
            {FORM_VIEW_OPTIONS.map(option => (
              <button
                key={option.label}
                type="button"
                onClick={() => handleFormViewChange(option.value)}
                aria-pressed={activeFormView === option.value}
                className={`rounded-[16px] border px-4 py-2.5 text-left transition ${
                  activeFormView === option.value
                    ? "border-emerald-200 bg-emerald-50 shadow-sm"
                    : "border-transparent bg-slate-50/70 hover:border-slate-200 hover:bg-white"
                }`}
              >
                <p
                  className={`text-sm font-semibold ${
                    activeFormView === option.value
                      ? "text-emerald-700"
                      : "text-slate-700"
                  }`}
                >
                  {option.label}
                </p>
                <p className="mt-0.5 text-xs leading-5 text-slate-500">
                  {option.description}
                </p>
              </button>
            ))}
          </div>
        )}

        <form
          key={data?.id ?? "new"}
          onSubmit={handleSubmit(onFormSubmit)}
          noValidate
        >
          {isBulkPreviewActive ? (
            <div className="space-y-4">
              <div className="rounded-[22px] border border-sky-100 bg-[linear-gradient(135deg,#f7fcff_0%,#ffffff_55%,#eff6ff_100%)] p-4 shadow-[0_16px_35px_-30px_rgba(59,130,246,0.22)]">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.14em] text-sky-700">
                      Bulk Upload
                    </p>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                      Upload one Excel sheet to add multiple maintenance expense
                      entries in a single request.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-[0_18px_40px_-30px_rgba(15,23,42,0.22)]">
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  className="hidden"
                  accept=".xlsx,.xls"
                  onChange={e => {
                    handleBulkExcelChange(e.target.files);
                    e.currentTarget.value = "";
                  }}
                />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-950">
                      Upload Spreadsheet
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Accepted formats: `.xlsx`. The file will be validated on
                      the server after upload.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleTemplateAction}
                    className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white"
                  >
                    Download Template
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => bulkFileInputRef.current?.click()}
                  className="mt-5 w-full rounded-[22px] border-2 border-dashed border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_55%,#eff6ff_100%)] px-6 py-12 text-center transition hover:border-sky-300 hover:bg-sky-50/40"
                >
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sky-100 text-sky-700 shadow-sm">
                    <svg
                      viewBox="0 0 24 24"
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeWidth="1.6"
                        d="M12 16V5m0 0l-4 4m4-4l4 4M5 19h14"
                      />
                    </svg>
                  </div>
                  <p className="mt-4 text-base font-semibold text-slate-900">
                    {bulkExcelFile ? bulkExcelFile.name : "Choose Excel file"}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    {bulkExcelFile
                      ? `${Math.max(1, Math.round(bulkExcelFile.size / 1024))} KB selected. Click to replace the file.`
                      : "Click here to select your maintenance expense spreadsheet from your computer."}
                  </p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {["XLSX"].map(fileType => (
                      <span
                        key={fileType}
                        className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm"
                      >
                        {fileType}
                      </span>
                    ))}
                  </div>
                </button>

                {bulkExcelError && (
                  <p className="mt-3 text-sm font-medium text-rose-600">
                    {bulkExcelError}
                  </p>
                )}

                {bulkExcelFile && (
                  <div className="mt-4 flex flex-col gap-3 rounded-[18px] border border-slate-200 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        Ready to upload
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        {bulkExcelFile.name}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={clearBulkExcelFile}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                    >
                      Remove File
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Row 1 */}
              <div className="mb-4 grid grid-cols-2 gap-3 max-sm:grid-cols-1">
                {/* Vehicle */}
                <Controller
                  name="vehicleId"
                  control={control}
                  render={({field, fieldState}) => {
                    const selectedOpt =
                      vehicles.find(
                        (v: any) => String(v.id) === String(field.value)
                      ) ?? null;
                    return (
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor="vehicleId"
                          className={`font-medium ${fieldState.invalid ? "text-[#f31260]" : "text-gray-700"}`}
                        >
                          Vehicle No{" "}
                          <span className="text-[#EF4444]" aria-hidden="true">
                            *
                          </span>
                        </label>
                        <Select
                          inputId="vehicleId"
                          value={selectedOpt}
                          onChange={(opt: any) => {
                            field.onChange(opt ? String(opt.id) : "");
                            clearErrors("vehicleId");
                          }}
                          options={vehicles}
                          getOptionLabel={(e: any) => e.regNo}
                          getOptionValue={(e: any) => String(e.id)}
                          placeholder="Select Vehicle"
                          styles={{
                            control: base => ({
                              ...base,
                              minHeight: "43px",
                              borderWidth: "2px",
                              borderColor: fieldState.invalid
                                ? "#f31260"
                                : "#d4d4d8",
                              borderRadius: "12px",
                              paddingLeft: "12px",
                              paddingRight: "12px",
                              boxShadow: "none",
                              fontSize: "14px",
                              backgroundColor: "white"
                            }),
                            indicatorSeparator: () => ({display: "none"}),
                            menu: base => ({
                              ...base,
                              borderRadius: "10px",
                              zIndex: 50
                            })
                          }}
                        />
                        {fieldState.error && (
                          <p
                            className="text-xs text-[#f31260]"
                            id="vehicleId-error"
                          >
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    );
                  }}
                />

                {/* Date */}
                <Controller
                  name="date"
                  control={control}
                  render={({field: {ref, value, onChange, ...fieldRest}}) => (
                    <NeoDatePicker
                      {...fieldRest}
                      label={
                        <>
                          Date{" "}
                          <span className="text-[#EF4444]" aria-hidden="true">
                            *
                          </span>
                        </>
                      }
                      placeholder="Please select from date"
                      errorMessage={errors.date && `${errors.date.message}`}
                      maxDate={maxDate}
                      value={value}
                      handleDateChange={(e: any) => {
                        onChange(e);
                        onDateChange("date", e);
                      }}
                    />
                  )}
                />
              </div>

              {/* Row 2 */}
              <div className="mb-4 grid grid-cols-2 gap-3 max-sm:grid-cols-1">
                {/* Service Types */}
                <Controller
                  name="expenseType"
                  control={control}
                  render={({field, fieldState}) => {
                    const selectedOpt =
                      servicetypes.find(
                        (v: any) => String(v.id) === String(field.value)
                      ) ?? null;
                    return (
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor="expenseType"
                          className={`-mt-1 mb-1 flex items-center justify-between font-medium ${fieldState.invalid ? "text-[#f31260]" : "text-gray-700"}`}
                        >
                          <span>
                            Service Types{" "}
                            <span className="text-[#EF4444]" aria-hidden="true">
                              *
                            </span>
                          </span>
                          <div
                            title="Add new service"
                            className="inline-flex cursor-pointer items-center text-xs text-[#3B82F6]"
                            onClick={() => openPopup(3)}
                          >
                            <PlusIcon size={15} />
                            Add Service
                          </div>
                        </label>
                        <Select
                          inputId="expenseType"
                          value={selectedOpt}
                          onChange={(opt: any) => {
                            field.onChange(opt ? String(opt.id) : "");
                            clearErrors("expenseType");
                          }}
                          options={servicetypes}
                          getOptionLabel={(e: any) => e.name}
                          getOptionValue={(e: any) => String(e.id)}
                          placeholder="Select service types"
                          styles={{
                            control: base => ({
                              ...base,
                              minHeight: "43px",
                              borderWidth: "2px",
                              borderColor: fieldState.invalid
                                ? "#f31260"
                                : "#d4d4d8",
                              borderRadius: "12px",
                              paddingLeft: "12px",
                              paddingRight: "12px",
                              boxShadow: "none",
                              fontSize: "14px",
                              backgroundColor: "white"
                            }),
                            indicatorSeparator: () => ({display: "none"}),
                            menu: base => ({
                              ...base,
                              borderRadius: "10px",
                              zIndex: 50
                            })
                          }}
                        />
                        {fieldState.error && (
                          <p
                            className="text-xs text-[#f31260]"
                            id="vehicleId-error"
                          >
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    );
                  }}
                />

                {/* Total Amount */}
                <Controller
                  name="totalAmount"
                  control={control}
                  render={({field}) => (
                    <NeoTextBox
                      label={
                        <>
                          Amount{" "}
                          <span className="text-[#EF4444]" aria-hidden="true">
                            *
                          </span>
                        </>
                      }
                      id="totalAmount"
                      placeholder="0.00"
                      color={errors.totalAmount ? "danger" : "default"}
                      isInvalid={!!errors.totalAmount}
                      errorMessage={errors.totalAmount?.message as string}
                      {...field}
                      value={
                        field.value !== undefined && field.value !== null
                          ? String(field.value)
                          : ""
                      }
                    />
                  )}
                />
              </div>

              {/* Row 3 */}
              <div className="mb-4 grid grid-cols-2 gap-3 max-sm:grid-cols-1">
                {/* Vendors */}
                <Controller
                  name="vendorId"
                  control={control}
                  render={({field, fieldState}) => {
                    const valueId =
                      field.value == null || String(field.value) === ""
                        ? null
                        : String(field.value);
                    const selectedOption = valueId
                      ? (vendors.find((o: any) => String(o.id) === valueId) ??
                        null)
                      : null;
                    return (
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor="vendorId"
                          className="-mt-1 mb-1 flex items-center justify-between"
                        >
                          Vendor/Workshop
                          <div
                            title="Add new vendor"
                            className="inline-flex cursor-pointer items-center text-xs text-[#3B82F6]"
                            onClick={() => openPopup(2)}
                          >
                            <PlusIcon size={15} />
                            Add Vendor
                          </div>
                        </label>
                        <Select
                          inputId="vendorId"
                          name={field.name}
                          value={selectedOption}
                          options={vendors}
                          onChange={(opt: any) =>
                            field.onChange(opt ? String(opt.id) : null)
                          }
                          getOptionLabel={(e: any) => e.name}
                          getOptionValue={(e: any) => String(e.id)}
                          placeholder="Select Vendor/Workshop"
                          styles={{
                            control: base => ({
                              ...base,
                              minHeight: "43px",
                              borderWidth: "2px",
                              borderColor: "#d4d4d8",
                              borderRadius: "12px",
                              paddingLeft: "12px",
                              paddingRight: "12px",
                              boxShadow: "none",
                              fontSize: "14px",
                              backgroundColor: "white"
                            }),
                            indicatorSeparator: () => ({display: "none"}),
                            menu: base => ({
                              ...base,
                              borderRadius: "10px",
                              zIndex: 50
                            })
                          }}
                        />
                      </div>
                    );
                  }}
                />
              </div>

              {/* Row 4 */}
              <div className="mb-4 grid grid-cols-1 gap-3 max-sm:grid-cols-1">
                {/* Note */}
                <label htmlFor="Description">Description</label>
                <Controller
                  name="description"
                  control={control}
                  render={({field}) => (
                    <>
                      <textarea
                        {...field}
                        value={field.value ?? ""}
                        className="w-full rounded-[17px] border-2 p-3 shadow-none hover:border-gray-600 focus:outline-none focus:ring-[#888888]"
                        placeholder="Describe the maintenance work performed"
                        rows={4}
                        id="description"
                        autoComplete="off"
                        {...register("description")}
                      />
                      {typeof errors.description?.message === "string" && (
                        <p className="mt-1 text-sm text-red-500">
                          {errors.description.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>

              {/* Attachments */}
              {isCreateMode && (
                <div className="mb-4">
                  <UploadFormField
                    value={attachments}
                    onChangeFile={setAttachments}
                    max={3}
                    variant="maintenance"
                    className="grid-cols-1 xl:grid-cols-[minmax(0,1.1fr)_minmax(260px,0.9fr)]"
                    onError={msg => console.error(msg)}
                  />
                </div>
              )}
            </>
          )}
          {/* Actions */}
          <div className="flex items-center justify-end gap-2 p-4">
            {isBulkPreviewActive ? (
              <>
                <NeoButton
                  type="button"
                  color="white"
                  onClick={() => handleFormViewChange("single")}
                >
                  Back to Form
                </NeoButton>
                <NeoButton
                  type="button"
                  color="black"
                  onClick={handleBulkUploadSubmit}
                  disabled={!bulkExcelFile || isBulkUploading}
                  isLoading={isBulkUploading}
                  spinner={
                    <AiOutlineLoading
                      size="xs"
                      className="animate-spin h-6 w-6"
                    />
                  }
                >
                  Upload File
                </NeoButton>
              </>
            ) : (
              <>
                <NeoButton type="button" color="white" onClick={onCancel}>
                  Reset
                </NeoButton>
                <NeoButton
                  type="submit"
                  disabled={isSubmitting || loading}
                  isLoading={isSubmitting || loading}
                  spinner={
                    <AiOutlineLoading
                      size="xs"
                      className="animate-spin h-6 w-6"
                    />
                  }
                  color="black"
                >
                  Save
                </NeoButton>
              </>
            )}
          </div>
        </form>
      </div>
      <NeoModal
        onOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        actionType={modalName}
        hasActions={false}
        data={modalData}
        onModalSubmit={handleFormSubmit}
        actionText={
          Number(expenseSettingType) === 2 ? "Vendor" : "Service Type"
        }
      />
    </>
  );
};
